import { DragEvent, FC, useMemo, useRef, useState } from "react";
import { FileTextIcon, CheckIcon, UploadIcon, Loader2Icon } from "lucide-react";

import { clsxMerge } from "shared/lib/helpers";
import {
  detectCallContextSource,
  estimateCallMinutes,
} from "shared/lib/helpers/call-context";
import { LucideIconWithAIGradient } from "shared/ui/icons/lucide";
import { Button } from "shared/ui/shadcn/button";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import {
  CALL_CONTEXT_MAX_LENGTH,
  CALL_CONTEXT_MIN_LENGTH,
  CALL_CONTEXT_SOURCE_LABELS,
  CallContextSourceT,
} from "shared/lib/interfaces/email/call-context";

export type CallContextSaveStatusT = "idle" | "saving" | "saved" | "error";

const ACCEPTED_FILE_TYPES = ".txt,.md,.vtt,.srt,text/plain";

export interface CallContextPanelPropsI extends PropsWithClassNameI {
  source: CallContextSourceT;
  content: string;
  /** Content as last persisted, used for the dirty check. */
  savedContent?: string;
  status?: CallContextSaveStatusT;
  disabled?: boolean;
  onChangeSource: (source: CallContextSourceT) => void;
  onChangeContent: (content: string) => void;
  onSave: () => void;
  onClear?: () => void;
}

/**
 * Capture panel for the call an SDR just had.
 *
 * Deliberately a plain textarea. An earlier version auto-detected on paste,
 * autosaved on a debounce and then swapped itself for a read-only summary chip,
 * which meant the field went uneditable underneath the SDR mid-edit. The
 * textarea is now the only surface, it never swaps out, and saving is an
 * explicit click.
 */
export const CallContextPanel: FC<CallContextPanelPropsI> = ({
  className,
  source,
  content,
  savedContent = "",
  status = "idle",
  disabled,
  onChangeSource,
  onChangeContent,
  onSave,
  onClear,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [fileError, setFileError] = useState<string>();

  /**
   * Measured the way the server measures it: trimmed, then counted in code
   * points. `content.length` counts UTF-16 code units and never saw the trim,
   * so the panel disagreed with the backend in two directions - an emoji in a
   * pasted transcript counted twice against the 50k ceiling, and 79 characters
   * followed by spaces looked long enough to save and came back 400 from the
   * min=80 binding.
   */
  const length = useMemo(() => Array.from(content.trim()).length, [content]);
  const isTooShort = length > 0 && length < CALL_CONTEXT_MIN_LENGTH;
  const isOverLimit = length > CALL_CONTEXT_MAX_LENGTH;
  const isDirty = content !== savedContent;
  const isSaved = !isDirty && length > 0;

  const canSave =
    !disabled &&
    status !== "saving" &&
    isDirty &&
    length > 0 &&
    !isTooShort &&
    !isOverLimit;

  /** Classify only when text arrives in bulk; never fight a manual override. */
  const applyIncomingText = (text: string) => {
    const trimmed = text.trim();

    onChangeContent(trimmed);
    onChangeSource(detectCallContextSource(trimmed));
  };

  const readFile = (file: File) => {
    if (file.size > CALL_CONTEXT_MAX_LENGTH * 2) {
      setFileError("That file is too large. Paste the relevant part instead.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setFileError(undefined);
      applyIncomingText(String(reader.result || ""));
    };
    reader.onerror = () => setFileError("Could not read that file.");
    reader.readAsText(file);
  };

  const handleDrop = (e: DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      readFile(file);
    }
  };

  return (
    <section
      className={clsxMerge("rounded-lg bg-[#764BC70D] p-4", className)}
      aria-label="Call context"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <LucideIconWithAIGradient icon={FileTextIcon} size={16} />

          <h6 className="typography-body-4-medium">Call notes or transcript</h6>

          {isSaved && status !== "saving" && (
            <span className="animate-fadein typography-body-5-medium flex items-center gap-1 rounded bg-[#DFF4B4] px-2 py-0.5 text-black">
              <CheckIcon className="size-3" />
              Saved
            </span>
          )}

          {status === "saving" && (
            <span className="typography-body-5 flex items-center gap-1 text-black/60">
              <Loader2Icon className="size-3 animate-spin" />
              Saving
            </span>
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          size="xs"
          disabled={disabled}
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadIcon className="size-3" />
          Upload file
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];

            if (file) {
              readFile(file);
            }
          }}
        />
      </div>

      <p className="typography-body-5 mb-2 text-black/60">
        Paste the transcript or your own notes. The AI writes the email from
        this.
      </p>

      <textarea
        value={content}
        disabled={disabled}
        onChange={(e) => onChangeContent(e.target.value)}
        onPaste={(e) => {
          const text = e.clipboardData.getData("text");

          // Only take over a paste into an empty field, so pasting a correction
          // into existing text behaves like a normal textarea.
          if (text && !content) {
            e.preventDefault();
            applyIncomingText(text);
          }
        }}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDraggingOver(true);
        }}
        onDragLeave={() => setIsDraggingOver(false)}
        placeholder="Paste the call transcript here, or type what the prospect said..."
        aria-label={CALL_CONTEXT_SOURCE_LABELS[source]}
        aria-invalid={isOverLimit}
        className={clsxMerge(
          "min-h-[180px] w-full resize-y rounded-lg bg-white p-3",
          "typography-body-4 placeholder:text-black/40",
          "border border-[#E5E5E5]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#714CC9]/40",
          isDraggingOver && "border-[#714CC9] bg-[#764BC70D]",
          isOverLimit && "border-error-content",
          disabled && "cursor-not-allowed opacity-60"
        )}
      />

      <div className="mt-2 flex items-center justify-between gap-2">
        <span
          className={clsxMerge(
            "typography-body-5",
            isOverLimit ? "text-error-content" : "text-black/40"
          )}
        >
          {isOverLimit
            ? `${length.toLocaleString()} / ${CALL_CONTEXT_MAX_LENGTH.toLocaleString()} characters — too long`
            : length > 0
              ? `${CALL_CONTEXT_SOURCE_LABELS[source]} · ~${estimateCallMinutes(
                  content
                )} min · ${length.toLocaleString()} characters`
              : "Or drop a .txt file here"}
        </span>

        <div className="flex items-center gap-2">
          {!!content && !!onClear && (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              disabled={disabled || status === "saving"}
              onClick={onClear}
            >
              Clear
            </Button>
          )}

          <Button
            type="button"
            size="xs"
            disabled={!canSave}
            loading={status === "saving"}
            onClick={onSave}
          >
            {isSaved ? "Saved" : "Save"}
          </Button>
        </div>
      </div>

      {isTooShort && (
        <p className="animate-fadein typography-body-5 mt-2 text-black/60">
          Add at least {CALL_CONTEXT_MIN_LENGTH} characters before saving.
        </p>
      )}

      {!!fileError && (
        <p className="text-error-content animate-fadein typography-body-5 mt-2">
          {fileError}
        </p>
      )}

      {status === "error" && (
        <p className="text-error-content animate-fadein typography-body-5 mt-2">
          Could not save. Your text is still here — try again.
        </p>
      )}
    </section>
  );
};
